import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { createArrayOfNumber } from 'common/utils/array'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import merge from 'lodash/merge'
import { useGetGrowthStandard } from 'modules/comparisonPond/api/queries'
import { useGetPartials } from 'modules/food/api/queries'
import { useGetGrowthShrimpLineChart } from 'modules/growth/api/queries'
import { useGetXTitleLineChart, useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import { FONT_PRIMARY } from 'theme/typography'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { useShrimpEventAnnotations } from 'hooks/useShrimpEventAnnotations'

const Y_TICK_AMOUNT = 5

const GrowthShrimpLineChart = () => {
  const { t } = useTranslation('growth')
  const { data, isFetching } = useGetGrowthShrimpLineChart()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { data: comparisonGrowthStandardData } = useGetGrowthStandard([cycleId ?? ''])
  const theme = useTheme()
  const { data: partials } = useGetPartials()
  const eventAnnotations = useShrimpEventAnnotations(partials)
  const xTitle = useGetXTitleLineChart()
  const species = useSpeciesName()

  // Shrimp size (count/kg) per age for the tooltip — shrimp ponds only; a fish
  // pond's count/kg isn't meaningful and must not be shown (DOF-1348).
  // Gaps carry the last recorded size forward, like the weight line does.
  const sizeByAge = useMemo(() => {
    const map = new Map<number, number>()
    if (species !== SpeciesName.Shrimp || !data?.length) return map
    const recorded = new Map(data.map((d) => [Number(d.shrimpAge), d.shrimpCountPerKg]))
    const ages = data.map((d) => Number(d.shrimpAge))
    let last: number | undefined
    for (let age = Math.min(...ages); age <= Math.max(...ages); age++) {
      const size = recorded.get(age)
      if (size != null) last = size
      if (last != null) map.set(age, last)
    }
    return map
  }, [data, species])

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return []
    const actualAges = data.map((d) => Number(d.shrimpAge))
    const minAge = Math.min(...actualAges)
    const maxAge = Math.max(...actualAges)
    const ageRange = Array.from({ length: maxAge - minAge + 1 }, (_, i) => minAge + i)
    const standardMap = new Map(
      comparisonGrowthStandardData?.map((s) => [Number(s.age), s.weight_in_g])
    )
    const hasStandard = standardMap.size > 0
    const standardData = ageRange.map((age) => ({ x: age, y: standardMap.get(age) ?? null }))
    const growthMap = new Map(data.map((d) => [Number(d.shrimpAge), d.weightPerShrimp]))
    const growthDataWithGaps = ageRange.map((age) => ({
      x: age,
      y: growthMap.get(age) ?? null,
    }))

    let lastValidValue: number | null = null
    const filledGrowthData = growthDataWithGaps.map((point) => {
      if (point.y != null) {
        lastValidValue = point.y
        return point
      }
      return { x: point.x, y: lastValidValue }
    })

    const result = [
      {
        name: t('growth.weight', { speciesName: species }),
        data: filledGrowthData,
      },
    ]

    if (hasStandard) {
      result.push({
        name: t('standard'),
        data: standardData,
      })
    }

    return result
  }, [data, comparisonGrowthStandardData, t])

  const chartOptions = merge(BaseOptionChart({ showPartialAnnotation: true }), {
    chart: {
      type: 'line',
    },
    xaxis: {
      type: 'numeric',
      tickAmount: dynamicTickAmount(data?.length || 0),
      labels: {
        formatter: integerFormatter,
      },
      title: { text: xTitle },
    },
    colors: [theme.palette.primary.main, '#D3D3D3'],
    yaxis: {
      title: { text: t('growth.unit') },
      min: undefined,
      max: undefined,
      tickAmount: Y_TICK_AMOUNT,
      forceNiceScale: true,
      labels: {
        style: {
          fontFamily: FONT_PRIMARY,
        },
        formatter: (val: number) => {
          if (typeof val !== 'number' || isNaN(val)) return '0.00'
          const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val)
          return formatted
        },
      },
    },

    tooltip: {
      shared: true,
      marker: {
        show: true,
      },
      x: {
        show: true,
        formatter: (v: number) => {
          const title = `${t('age', { speciesName: species })}: ${v} ${t('unit.day')}`
          const size = sizeByAge.get(v)
          if (size == null) return title
          // Apex renders the tooltip title as HTML; the values are API numbers.
          const sizeText = t('tooltip.size', {
            speciesName: species,
            value: new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(size),
          })
          return `${title}<div style="font-weight:400;margin-top:2px">${sizeText}</div>`
        },
      },
      y: {
        formatter: (val: number) => {
          if (typeof val !== 'number' || isNaN(val)) return `0.00 (${t('gram.per.one')})`
          const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val)
          return `${formatted} (${t('gram.per.one')})`
        },
      },
    },
    markers: {
      size: 0,
      hover: { sizeOffset: 4 },
    },
    stroke: {
      curve: 'straight',
      width: 2,
      dashArray: [0, 5],
    },
    annotations: {
      xaxis: eventAnnotations,
    },
  })

  if (isFetching)
    return (
      <Stack spacing={3} p={2}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {createArrayOfNumber(3).map((i) => (
            <Skeleton width={60} key={i} />
          ))}
        </Stack>
        {createArrayOfNumber(6).map((i) => (
          <Stack direction="row" key={i} spacing={1}>
            <Skeleton width={40} />
            <Skeleton width="100%" />
          </Stack>
        ))}
        <Stack direction="row" spacing={2}>
          {createArrayOfNumber(6).map((i) => (
            <Skeleton key={i} width="100%" />
          ))}
        </Stack>
      </Stack>
    )

  return <ReactApexChart type="line" series={chartData} options={chartOptions} height={420} />
}

export default GrowthShrimpLineChart